import type { VerticalNavItems } from '@/@layouts/types';
import { REPORT_PERMISSION_KEYS } from '@/constants/roles';
import { hasRequiredPermission, isPowerRole } from '@/router/guards/permission.guard';
import { useAuthStore } from '@/stores/agency/auth';
import { useUserManagementStore } from '@/stores/agency/settings/user-management/user-management';

const agencyNavItems: VerticalNavItems = [
  {
    title: 'Home',
    to: { name: 'agency-home' },
    icon: { icon: 'tabler-home' },
    permissionKey: 'dashboard.access'
  },
  {
    title: 'Clients',
    to: { name: 'agency-clients-list' },
    icon: { icon: 'tabler-user-check' },
    children: [
      {
        title: 'Client List',
        to: { name: 'agency-clients-list' },
        icon: { icon: 'tabler-list' },
        permissionKey: 'clients.access'
      },
      {
        title: 'Client Logs',
        to: { name: 'agency-clients-log' },
        icon: { icon: 'tabler-list-details' },
        permissionKey: 'clients.access'
      }
    ]
  },
  {
    title: 'Carers',
    to: { name: 'agency-carers-list' },
    icon: { icon: 'tabler-user-heart' },
    children: [
      {
        title: 'Carer List',
        to: { name: 'agency-carers-list' },
        icon: { icon: 'tabler-list' },
        permissionKey: 'carers.access'
      },
      {
        title: 'Availability',
        to: { name: 'agency-carers-availability' },
        icon: { icon: 'tabler-calendar-week' },
        permissionKey: 'carers.access'
      },
      {
        title: 'Review Documents',
        to: { name: 'agency-carers-documents' },
        icon: { icon: 'tabler-file-text' },
        permissionKey: 'carers.access'
      },
      {
        title: 'Absence Requests',
        to: { name: 'agency-carers-leave-request' },
        icon: { icon: 'tabler-calendar-off' },
        permissionKey: 'carers.access'
      }
    ]
  },
  {
    title: 'Rota',
    to: { name: 'agency-rota' },
    icon: { icon: 'tabler-tilt-shift' },
    children: [
      {
        title: 'Rolling Rota',
        to: { name: 'agency-rota-client-rota' },
        icon: { icon: 'tabler-user-shield' },
        permissionKey: 'rota.access'
      },
      {
        title: 'Publish Rota',
        to: { name: 'agency-rota-publish-rota' },
        icon: { icon: 'tabler-transfer-in' },
        permissionKey: 'publishrota.read'
      }
    ]
  },
  {
    title: 'Schedule',
    to: { name: 'agency-schedule' },
    icon: { icon: 'tabler-calendar-check' },
    children: [
      {
        title: 'Carer View',
        to: { name: 'agency-schedule-carer' },
        icon: { icon: 'tabler-user-heart' },
        permissionKey: 'schedule.access'
      },
      {
        title: 'Client View',
        to: { name: 'agency-schedule-client' },
        icon: { icon: 'tabler-user-check' },
        permissionKey: 'schedule.access'
      },
      {
        title: 'List View',
        to: { name: 'agency-schedule-list-view' },
        icon: { icon: 'tabler-list' },
        permissionKey: 'schedule.access'
      },
      {
        title: 'Shift Requests',
        to: { name: 'agency-schedule-shift-request' },
        icon: { icon: 'tabler-arrow-autofit-left' },
        permissionKey: 'schedule.access'
      }
    ]
  },
  {
    title: 'Finance',
    to: { name: 'agency-finance' },
    icon: { icon: 'tabler-settings-dollar' },
    children: [
      {
        title: 'Timesheets',
        to: { name: 'agency-finance-timesheets' },
        icon: { icon: 'tabler-file-text' },
        permissionKey: 'finance.timesheets.read'
      },
      {
        title: 'Client Invoices',
        to: { name: 'agency-finance-client-invoices' },
        icon: { icon: 'tabler-file-dollar' },
        permissionKey: 'finance.invoices.read'
      },
      {
        title: 'Carer Payrolls',
        to: { name: 'agency-finance-carer-payroll' },
        icon: { icon: 'tabler-cash' },
        permissionKey: 'finance.payroll.read'
      }
    ]
  },
  {
    title: 'Reports',
    to: { name: 'agency-reports' },
    icon: { icon: 'tabler-chart-histogram' },
    permissionKey: REPORT_PERMISSION_KEYS
  }
];

const contactNavItems: VerticalNavItems = [
  {
    title: 'Home',
    to: { name: 'contact-dashboard' },
    icon: { icon: 'tabler-home' }
  },
  {
    title: 'Clients',
    to: { name: 'contact-clients' },
    icon: { icon: 'tabler-users' }
  }
];

const branchNavItems: VerticalNavItems = [
  {
    title: 'Home',
    to: { name: 'branch-home' },
    icon: { icon: 'tabler-home' }
  },
  {
    title: 'User Management',
    to: { name: 'branch-user-management' },
    icon: { icon: 'tabler-users' }
  },
  {
    title: 'Branch Management',
    to: { name: 'branch-branch-management' },
    icon: { icon: 'tabler-building-skyscraper' }
  }

  // {
  //   title: 'Reports',
  //   to: { name: 'branch-reports' },
  //   icon: { icon: 'tabler-report' }
  // }
];

/**
 * Role to menu mapping for better performance
 */
const ROLE_MENU_MAP: Record<string, VerticalNavItems> = {
  superadmin: agencyNavItems,
  admin: agencyNavItems,
  manager: agencyNavItems,
  'care coordinator': agencyNavItems,
  'field supervisor': agencyNavItems,
  'point of contact': contactNavItems
};

/**
 * Filters menu items based on user permissions
 * Optimized with single-pass filtering and reduced object creation
 */
const filterMenuByPermissions = (
  items: VerticalNavItems,
  permissionChecker: (key: string) => boolean
): VerticalNavItems => {
  const result: VerticalNavItems = [];

  for (const item of items) {
    // Skip section titles (they don't have permissionKey)
    if ('heading' in item) {
      result.push(item);
      continue;
    }

    // Skip items without permission
    if (item.permissionKey && !hasRequiredPermission(item.permissionKey, permissionChecker)) {
      continue;
    }

    // Handle items with children
    if ('children' in item && item.children?.length) {
      const filteredChildren = filterMenuByPermissions(item.children, permissionChecker);

      // Only include parent if it has visible children
      if (filteredChildren.length > 0) {
        result.push({
          ...item,
          children: filteredChildren
        });
      }
    } else {
      // Include items without children
      result.push(item);
    }
  }

  return result;
};

export const useVerticalNavByRole = (role: string): VerticalNavItems => {
  const normalizedRole = role?.toLowerCase();
  const menu = ROLE_MENU_MAP[normalizedRole];

  if (!menu) return [];

  // Power roles get full menu without filtering
  const authStore = useAuthStore();
  if (isPowerRole(authStore.loggedInUserProfile?.role?.slug)) {
    return menu;
  }

  // Filter menu based on permissions (only initialize store when needed)
  const permissionStore = useUserManagementStore();

  return filterMenuByPermissions(menu, key => permissionStore.userHasPermission(key));
};

export function useVerticalNavByRoleBranch(role: string): VerticalNavItems {
  switch (role?.toLowerCase()) {
    case 'superadmin':
      return branchNavItems;

    default:
      return [];
  }
}
